/**
 * Central source of truth for KPI engine default values.
 *
 * Every magic number that previously lived as a `?? N` fallback inside an
 * engine file is now sourced from here. Per-instance config still wins —
 * these are only the defaults used when an instance leaves a field unset.
 *
 * Why centralize:
 *   - One file to audit when product asks "why is the default warning at 30
 *     days?" or "what counts as 'no movement' for the trend gate?"
 *   - One place to change the platform-wide default. Per-instance overrides
 *     remain available for project owners who want stricter or looser bands.
 *   - Test fixtures can import the constant directly so tests don't drift
 *     from the engine's defaults silently.
 *
 * Adding a new default:
 *   - Add the field under the appropriate sub-namespace below
 *   - Reference it from the engine's `?? KPI_DEFAULTS.<area>.<field>` site
 *   - Document the rationale inline if non-obvious
 *
 * Skip's principle (DEV-814): fewer redundant fields. If a number lives in
 * two places, they will eventually disagree — usually after a hot fix
 * during a demo. Centralizing here makes that class of drift impossible.
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2 (centralization step).
 */

export const KPI_DEFAULTS = {
  /* ----------------------------------------------------------------------- */
  /*  Schedule — % complete variance (baseline vs actual)                    */
  /* ----------------------------------------------------------------------- */
  schedule: {
    /** Behind by >= this many percentage points → yellow band. */
    yellow_threshold_pp: 5,
    /** Behind by >= this many percentage points → red band. */
    red_threshold_pp: 15,
    /** Behind by >= this many percentage points → score floors at 0. */
    score_zero_at_behind_pp: 40,
    /**
     * Trend gate (DEV-814):
     * The user-input override treats a delta in actual_complete smaller than
     * this as "no new data entered". Bumped from 0.25 → 1.0 to forgive small
     * baseline-driven reporting drift; per-instance override still available
     * via `cfg.trend_epsilon_pp`.
     */
    trend_epsilon_pp: 1.0,
  },

  /* ----------------------------------------------------------------------- */
  /*  Schedule — Schedule Performance Index (EVM-lite)                       */
  /* ----------------------------------------------------------------------- */
  spi: {
    /** SPI >= this is green. */
    warning_threshold: 0.95,
    /** SPI < this is red. */
    critical_threshold: 0.90,
    /**
     * Suppress SPI display when planned% is below this — prevents wild
     * swings driven by a near-zero denominator at project start.
     */
    early_maturity_guard_pct: 5,
  },

  /* ----------------------------------------------------------------------- */
  /*  Budget — % over baseline budget (cost variance)                        */
  /* ----------------------------------------------------------------------- */
  budget: {
    /** Over budget by >= this % → yellow band. */
    yellow_threshold_pct: 5,
    /** Over budget by >= this % → red band. */
    red_threshold_pct: 10,
    /** Over budget by >= this % → score floors at 0. */
    score_zero_at_over_pct: 40,
    /** Trend gate epsilon for over-budget % (legacy budget engine). */
    trend_epsilon_pct: 0.25,
  },

  /* ----------------------------------------------------------------------- */
  /*  Budget — Cost Performance Index (EVM-lite)                             */
  /* ----------------------------------------------------------------------- */
  cpi: {
    /** CPI >= this is green. */
    warning_threshold: 0.95,
    /** CPI < this is red. */
    critical_threshold: 0.90,
    /** Suppress CPI display when baseline budget is below this %. */
    early_maturity_guard_pct: 5,
  },

  /* ----------------------------------------------------------------------- */
  /*  Mission — deadline-proximity score degradation                         */
  /*  Shared by all sub-KPI types that support deadlines.                    */
  /* ----------------------------------------------------------------------- */
  deadline: {
    /** Within this many days of deadline → score caps at warning_score_cap. */
    warning_days: 30,
    /** Within this many days of deadline → score caps at critical_score_cap. */
    critical_days: 7,
    /**
     * Score cap when inside the warning window. Chosen to land cleanly in
     * the yellow band given default thresholds (yellow_threshold=60,
     * green_threshold=80). 75 is unambiguously yellow.
     *
     * Was 80 prior to DEV-814 — that value collided with green_threshold
     * (80), so a deadline-degraded score showed GREEN instead of yellow,
     * defeating the purpose of the warning window. Skip caught this for
     * Binary and confirmed the same bug applies to all types.
     */
    warning_score_cap: 75,
    /**
     * Score cap when inside the critical window. Chosen to land cleanly in
     * the red band (below yellow_threshold=60). 50 is unambiguously red.
     * Was 70 prior to DEV-814 (which mapped to yellow, not red).
     */
    critical_score_cap: 50,
    /** Score when the deadline has passed without the goal being met. */
    overdue_score: 0,
  },

  /* ----------------------------------------------------------------------- */
  /*  Mission — type-specific color thresholds                               */
  /*  Per-instance config can override; these are the platform defaults.     */
  /* ----------------------------------------------------------------------- */
  mission: {
    /** Universal score band cutoffs (Count / Improvement / Rate / Reduction). */
    bands: {
      green_threshold: 80,
      yellow_threshold: 60,
    },
    /**
     * Milestone Set uses a more forgiving yellow band — partial completion
     * (5 of 10) is still meaningful progress, not a critical state.
     */
    milestone_set_bands: {
      green_threshold: 80,
      yellow_threshold: 50,
    },
    /**
     * Binary's discrete score values ARE the band signal — do NOT add
     * separate thresholds. Listed here for completeness and so the legacy
     * engine's color shim has a documented source for these constants.
     *
     * Aligned with deadline.warning_score_cap and critical_score_cap so a
     * Binary KPI in the warning window scores 75 (renders yellow under the
     * default yellow=60/green=80 bands) and in the critical window scores
     * 50 (renders red). Pre-DEV-814 these were 80/70 which collided with
     * the green threshold and rendered green/yellow respectively — wrong.
     */
    binary_score_levels: {
      achieved: 100,
      warning_window: 75,
      critical_window: 50,
      failed: 0,
    },
    /**
     * Required-completion default for Milestone Set (used when instance
     * config omits `required_completion_pct`). 100 = "all milestones must
     * be hit by deadline".
     */
    milestone_set_required_completion_pct: 100,
    /**
     * Color → score mapping used when a Mission sub-KPI is configured with
     * color_mode === 'deadline'. The KPI's color is driven by time-to-
     * deadline (deadline windows), and its numeric score for the Mission
     * weighted rollup is DERIVED from that color.
     *
     * Skip's choice (DEV-814): keep the bands forgiving so a single KPI
     * doesn't crater the Mission category just because its deadline is
     * tight. 100 → green, 75 → yellow, 50 → red, 50 → overdue (same as
     * red — there is no harder failure tier in deadline mode).
     *
     * Why no 0: an overdue unmet KPI is still recoverable in many
     * project contexts. The signal value is "this is red"; the score
     * value just feeds the weighted average. 0 was rejected as too
     * punishing for the rollup.
     */
    color_mode_deadline_scores: {
      green: 100,
      yellow: 75,
      red: 50,
      overdue: 50,
    },
    /** Default color_mode by mission type. Binary is locked to deadline. */
    color_mode_defaults: {
      binary: 'deadline',
      count: 'score',
      improvement: 'score',
      reduction: 'score',
      rate: 'score',
      milestone_set: 'score',
    },
    /**
     * Pace mode (DEV-815): default tolerances for "behind expected pace by N units".
     * Concrete units are intentional — Skip's preference for small Count KPIs
     * (Interns Hired with target 6). For larger targets, project owners override
     * per instance. Future enhancement: pace_tolerance_mode='percent'.
     *
     * Yellow at 1 unit behind, red at 2 — gives meaningful signal on small
     * targets without being trigger-happy.
     */
    pace_defaults: {
      yellow_units_behind: 1,
      red_units_behind: 2,
    },
  },
} as const;

export type ColorMode = 'score' | 'deadline' | 'pace';

/**
 * Strongly-typed alias for downstream consumers that want a non-readonly
 * structural shape (e.g., for `Partial<>` overrides). `KPI_DEFAULTS` itself
 * is `as const`, so reading from it preserves narrowed types at call sites.
 */
export type KpiDefaults = typeof KPI_DEFAULTS;
